/**
 * What Claude returns for one document. The zod schema is sent to the API as
 * the structured-output format and validates the reply on the way back.
 *
 * The API allows at most 16 nullable fields in a schema, so optional text is
 * a plain string where "" means "not stated"; null is kept for numbers,
 * dates and objects. Read optional text with `||`, not `??`.
 */
import * as z from 'zod/v4'
import { isIsoDate } from './dates'

const isoDate = z.string().refine(isIsoDate, 'must be YYYY-MM-DD')

export const DOC_TYPES = [
  'bank_statement',
  'credit_card_statement',
  'brokerage_statement',
  'retirement_statement',
  'loan_statement',
  'payslip',
  'tax_form',
  'insurance',
  'receipt',
  'other',
] as const

export const ACCOUNT_KINDS = [
  'checking',
  'savings',
  'credit',
  'brokerage',
  'ira',
  'roth_ira',
  '401k',
  'hsa',
  '529',
  'mortgage',
  'auto_loan',
  'student_loan',
  'other',
] as const

export const CATEGORIES = [
  'income',
  'transfer',
  'housing',
  'utilities',
  'groceries',
  'dining',
  'transport',
  'fuel',
  'travel',
  'shopping',
  'health',
  'insurance',
  'entertainment',
  'subscriptions',
  'education',
  'childcare',
  'gifts_donations',
  'fees',
  'interest',
  'taxes',
  'investments',
  'other',
] as const

export const ASSET_CLASSES = [
  'us_stock',
  'intl_stock',
  'bond',
  'cash',
  'real_estate',
  'commodity',
  'crypto',
  'target_date',
  'other',
] as const

/** What a card or loan statement says is owed next, for the bill reminders. */
const billFields = {
  payment_due_date: isoDate.nullable().describe('Payment due date printed on a credit card or loan statement; null on other documents'),
  minimum_payment: z
    .number()
    .nullable()
    .describe('Minimum payment due on a card, or the amount due on a loan, as a positive number; null if not printed'),
  autopay: z.boolean().nullable().describe('true if the statement says automatic payments are set up, false if it says they are not, null if it does not say'),
}

/** Only the bill fields, for re-reading a statement read before they existed. */
export const BillsSchema = z.object({
  bills: z.array(
    z.object({
      account_last4: z.string().describe('last4 of the card or loan this belongs to; "" when the document covers one account'),
      ...billFields,
    }),
  ),
})
export type Bills = z.infer<typeof BillsSchema>

/**
 * What Claude returns for one document. `categories` are the ids it may file a
 * transaction under: the household's Categories tab (see categories.ts).
 */
export const extractionSchema = (categories: readonly [string, ...string[]]) => z.object({
  doc_type: z.enum(DOC_TYPES),
  confidence: z.number().describe('0 to 1: how sure you are the fields below are right'),
  review_reason: z.string().describe('Why a person should look at this, or "" if nothing needs checking'),
  institution: z.string().describe('Bank, broker, employer, merchant or agency, as commonly named, or ""'),
  short_title: z
    .string()
    .describe('A few words for the filename, without date or institution, e.g. "Sapphire Reserve statement", "1099-B", "receipt"'),
  document_date: isoDate.nullable().describe('Statement period end, or the date the document was issued'),
  period_start: isoDate.nullable(),
  summary: z.string().describe('One or two sentences on what this document is and anything notable in it'),
  accounts: z
    .array(
      z.object({
        kind: z.enum(ACCOUNT_KINDS),
        name: z.string().describe('Product name, e.g. "Sapphire Reserve", "Rollover IRA", or ""'),
        last4: z.string().describe('Last 4 digits of the account number only, or ""'),
      }),
    )
    .describe('Every account the document covers: usually one, several for a combined statement, none for a receipt'),
  balances: z.array(
    z.object({
      account_last4: z.string().describe('last4 of the account in accounts this belongs to; "" when the document covers one account'),
      as_of: isoDate,
      balance: z.number().describe('Amount owed is negative for credit cards and loans'),
      currency: z.string(),
      credit_limit: z.number().nullable(),
      apr: z.number().nullable().describe('Percent, e.g. 24.99'),
      ...billFields,
    }),
  ),
  transactions: z.array(
    z.object({
      account_last4: z.string().describe('last4 of the account in accounts this belongs to; "" when the document covers one account'),
      date: isoDate,
      description: z.string(),
      merchant: z.string().describe('Cleaned-up merchant name, or ""'),
      amount: z.number().describe('Negative = money out of the account holder\'s pocket, positive = money in'),
      category: z.enum(categories),
    }),
  ),
  holdings: z.array(
    z.object({
      account_last4: z.string().describe('last4 of the account in accounts this belongs to; "" when the document covers one account'),
      as_of: isoDate,
      symbol: z.string().describe('Ticker, or "" if none'),
      name: z.string(),
      quantity: z.number().nullable(),
      price: z.number().nullable(),
      value: z.number(),
      cost_basis: z.number().nullable(),
      asset_class: z.enum(ASSET_CLASSES),
      expense_ratio: z.number().nullable().describe('Percent, e.g. 0.03, only if printed on the document'),
    }),
  ),
  receipt: z
    .object({
      date: isoDate,
      merchant: z.string(),
      total: z.number(),
      tax: z.number().nullable(),
      items: z.array(z.object({ description: z.string(), amount: z.number() })),
    })
    .nullable(),
})

export const ExtractionSchema = extractionSchema(CATEGORIES)

/** Category is any id from the tab, not only the built-ins. */
export type Extraction = Omit<z.infer<typeof ExtractionSchema>, 'transactions'> & {
  transactions: (Omit<z.infer<typeof ExtractionSchema>['transactions'][number], 'category'> & { category: string })[]
}
